import { useEffect, useState, type FormEvent } from 'react'
import { motion } from 'framer-motion'
import {
  Search,
  Filter,
  Briefcase,
  AlertCircle,
  ShieldCheck,
  Users,
  Activity,
  ArrowUpRight,
} from 'lucide-react'
import { useCaseStore } from '../stores/caseStore'
import { AnalystNavigation } from '../components/Navigation'
import { CaseCard } from '../components/CaseCard'
import { EmptyState } from '../components/EmptyState'
import { cn } from '../lib/utils'
import type { Case } from '../types'

// §7 honest alerting: this dashboard renders REAL cases or an honest empty/
// error state. The old hardcoded demoCases fallback (five fabricated
// incidents shown whenever the API failed) is deliberately gone — never
// reintroduce invented detections (TASKS/analyst-escalation-view.md).

export function AnalystDashboard() {
  const { cases, filters, setFilters, isLoading, error } = useCaseStore()
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    void useCaseStore.getState().fetchCases()
  }, [])

  // Filtered cases
  const filteredCases = cases.filter((c: Case) => {
    if (filters.status && c.status !== filters.status) return false
    if (filters.severity && c.severity < filters.severity) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        c.title.toLowerCase().includes(q) ||
        c.case_number.toLowerCase().includes(q) ||
        (c.principal_email?.toLowerCase().includes(q) ?? false)
      )
    }
    return true
  })

  // All values computed from REAL fetched cases — no invented metrics (the
  // old hardcoded "12 alerts today" is gone).
  const stats = {
    active: cases.filter((c) => c.status === 'open' || c.status === 'contained' || c.status === 'escalated').length,
    escalated: cases.filter((c) => c.status === 'escalated').length,
    tier0Actions: cases.filter((c) => c.autonomy_tier === 0).length,
    principals: new Set(cases.map((c) => c.principal_id)).size,
  }

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    // Search is reactive via the filteredCases logic
  }

  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Active Cases',
              value: stats.active,
              icon: Briefcase,
              color: 'text-brass',
              bg: 'bg-brass/10',
            },
            {
              label: 'Escalated',
              value: stats.escalated,
              icon: ArrowUpRight,
              color: 'text-info',
              bg: 'bg-info/10',
            },
            {
              label: 'Tier 0 Cases',
              value: stats.tier0Actions,
              icon: Activity,
              color: 'text-warning',
              bg: 'bg-warning/10',
            },
            {
              label: 'Principals',
              value: stats.principals,
              icon: Users,
              color: 'text-success',
              bg: 'bg-success/10',
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 p-4"
            >
              <div className="flex items-center gap-3">
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', stat.bg)}>
                  <stat.icon className={cn('w-4 h-4', stat.color)} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-2">
              <Filter className="w-4 h-4" aria-hidden="true" />
              Filters
            </div>

            {/* Status filter */}
            <select
              value={filters.status ?? ''}
              onChange={(e) => setFilters({ status: e.target.value || undefined })}
              className="tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="contained">Contained</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
            </select>

            {/* Severity filter */}
            <select
              value={filters.severity?.toString() ?? ''}
              onChange={(e) => setFilters({ severity: e.target.value ? Number(e.target.value) : undefined })}
              className="tap-min px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass"
              aria-label="Filter by minimum severity"
            >
              <option value="">All Severities</option>
              <option value="9">Critical (9+)</option>
              <option value="7">High (7+)</option>
              <option value="5">Medium (5+)</option>
              <option value="3">Low (3+)</option>
            </select>

            {/* Search */}
            <form onSubmit={handleSearch} className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cases..."
                  className="tap-min pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brass w-64"
                />
              </div>
              {filters.status || filters.severity ? (
                <button
                  type="button"
                  onClick={() => setFilters({ status: undefined, severity: undefined })}
                  className="tap-min px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-navy hover:bg-gray-100 transition-colors"
                >
                  Clear
                </button>
              ) : null}
            </form>
          </div>
        </div>

        {/* Case list */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger max-w-xl mx-auto">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                Could not load cases from the backend ({error}). Nothing is
                shown because nothing could be loaded — this console never
                invents incidents.
              </span>
            </div>
          ) : cases.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No cases"
              message="The team has not raised any cases for this tenant. Quiet is quiet — nothing is fabricated here."
            />
          ) : filteredCases.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No cases match"
              message="Try adjusting your filters or search criteria."
              action={{
                label: 'Clear filters',
                onClick: () => {
                  setFilters({ status: undefined, severity: undefined })
                  setSearchQuery('')
                },
              }}
            />
          ) : (
            <div role="list" aria-label="Case queue" className="space-y-3">
              {filteredCases.map((caseItem: Case, index: number) => (
                <CaseCard key={caseItem.id} caseItem={caseItem} index={index} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
